import type { ReactElement } from 'react';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api } from '../api';
import { writeStoredSession } from '../auth';

export function Login(): ReactElement {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();

  const submit = async (): Promise<void> => {
    setError(null);
    setSubmitting(true);
    try {
      const result = await api.login(email, password);
      // Persist session to localStorage so ProtectedRoute can find it
      // before the navigate() triggers a re-render.
      writeStoredSession({
        id: result.id,
        email,
        displayName: result.displayName,
        role: result.role,
        apiKeyMasked: `${result.api_key.slice(0, 8)}…`,
        apiKey: result.api_key,
      });
      navigate('/spaces');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'login failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="auth-page">
      <div className="auth-card">
        <h1 className="auth-brand">GimbalPortal</h1>
        <h2 className="text-lg font-semibold mb-4">Sign in</h2>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            void submit();
          }}
        >
          <label className="block text-xs text-slate-400 mb-1">Email</label>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full mb-3 bg-slate-900 border border-slate-700 rounded p-2"
            autoComplete="email"
          />
          <label className="block text-xs text-slate-400 mb-1">Password</label>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full mb-3 bg-slate-900 border border-slate-700 rounded p-2"
            autoComplete="current-password"
          />
          {error && <p className="text-rose-400 text-sm mb-3">{error}</p>}
          <button
            type="submit"
            disabled={submitting}
            className="w-full bg-accent text-slate-900 rounded py-2 font-medium disabled:opacity-50"
          >
            Sign in
          </button>
        </form>
        <p className="text-xs text-slate-400 mt-4 text-center">
          Don&apos;t have an account?{' '}
          <Link to="/register" className="text-accent hover:underline">
            Register
          </Link>
        </p>
      </div>
    </div>
  );
}
